/**
 * DataTable — every list in the CRM. A thin wrapper over Ant Design's <Table>
 * that bakes in our defaults so screens only describe columns and data.
 *
 * Defaults it adds:
 *   - sticky header; fits the page on desktop (cells wrap), horizontal scroll on narrow screens
 *   - skeleton rows while `loading`
 *   - <EmptyState> when there is no data
 *   - optional row selection (checkbox column)
 *   - controlled sorting: the SCREEN sorts the data (keeps its existing sort logic);
 *     the table only shows the arrow and reports clicks
 *   - pagination (50 per page, size changer) — pass pagination={false} to disable
 *   - row actions (<RowActions>) appear on row hover on mouse devices, always on touch
 *
 * Props:
 *   columns       Ant column defs. Extra field: `sortable: true` → header becomes clickable
 *   data          array of rows
 *   rowKey        string|fn  (default 'id')
 *   loading       bool
 *   sort          { key, order: 'ascend'|'descend' }   current sort (screen state)
 *   onSort        fn(key)   header clicked — screen toggles its own sort state
 *   selection     { selectedKeys, onChange(keys) }     enables the checkbox column
 *   empty         node      custom empty state (default: <EmptyState title="אין נתונים" />)
 *   onRowClick    fn(row)   whole-row click (clicks on buttons/links/checkboxes are ignored)
 *   pagination    Ant pagination config or false. Server-side paging: pass
 *                 { current, total, pageSize, onChange, showSizeChanger: false } with one page in `data`
 *   expandable    Ant expandable config, passed through ({ expandedRowRender, expandedRowKeys,
 *                 onExpand }) — rows that open a details panel under them
 *   fit           always fit the screen width, wrapping cells instead of scrolling sideways on
 *                 phones (for tables with few columns, e.g. the tag list)
 *
 * Usage:
 *   <DataTable
 *     columns={[{ key: 'name', title: 'שם', dataIndex: 'name', sortable: true }]}
 *     data={rows} sort={{ key: 'name', order: 'ascend' }} onSort={toggleSort}
 *     selection={{ selectedKeys, onChange: setSelectedKeys }}
 *   />
 */
import React, { useState } from 'react';
import { Table, Skeleton, Grid } from 'antd';
import EmptyState from './EmptyState';
import styles from './DataTable.css';

const DEFAULT_PAGE_SIZE = 50;

const DataTable = ({
  columns,
  data,
  rowKey = 'id',
  loading,
  sort,
  onSort,
  selection,
  empty,
  onRowClick,
  pagination,
  fit,
  expandable,
  className,
}) => {
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const screens = Grid.useBreakpoint();

  // `sortable` columns get a controlled sort arrow; the data itself is pre-sorted by the screen.
  const antColumns = columns.map((col) => {
    if (!col.sortable) return col;
    const { sortable, ...rest } = col;
    return {
      ...rest,
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      sortOrder: sort && sort.key === col.key ? sort.order : null,
      showSorterTooltip: false,
    };
  });

  const onChange = (_pagination, _filters, sorter, extra) => {
    if (extra && extra.action === 'sort' && onSort && sorter && sorter.columnKey) onSort(sorter.columnKey);
  };

  const rowSelection = selection
    ? {
        selectedRowKeys: selection.selectedKeys,
        onChange: (keys) => selection.onChange(keys),
        columnWidth: 48,
        preserveSelectedRowKeys: true,
      }
    : undefined;

  const isFirstLoad = loading && (!data || data.length === 0);

  // Server-side paging: the screen passes { current, total, pageSize, onChange } and `data` is
  // one page only — so hide the pager by `total`, not by the length of this page.
  const serverPaged = pagination && pagination.total !== undefined;
  const hidePager = serverPaged
    ? pagination.total <= (pagination.pageSize || DEFAULT_PAGE_SIZE)
    : data && data.length <= DEFAULT_PAGE_SIZE && pageSize === DEFAULT_PAGE_SIZE;

  const paginationConfig =
    pagination === false || hidePager
      ? false
      : {
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: [25, 50, 100, 250, 500],
          onShowSizeChange: (_current, size) => setPageSize(size),
          showTotal: (total, range) => `${range[0]}–${range[1]} מתוך ${total}`,
          position: ['bottomCenter'],
          ...(pagination || {}),
        };

  return (
    <div className={[styles.wrap, className].filter(Boolean).join(' ')}>
      {isFirstLoad ? (
        <div className={styles.skeleton}>
          <Skeleton active paragraph={{ rows: 8 }} title={false} />
        </div>
      ) : (
        <Table
          columns={antColumns}
          dataSource={data}
          rowKey={rowKey}
          loading={loading && !isFirstLoad}
          rowSelection={rowSelection}
          expandable={expandable}
          onChange={onChange}
          pagination={paginationConfig}
          sticky
          // Desktop: the table fits the page and long cells wrap (columns without a
          // width share the spare space). Narrow screens: scroll sideways instead.
          scroll={screens.lg || fit ? undefined : { x: 'max-content' }}
          rowClassName={() => styles.row}
          locale={{ emptyText: empty || <EmptyState title="אין נתונים" /> }}
          onRow={
            onRowClick
              ? (row) => ({
                  onClick: (e) => {
                    // React bubbles clicks from portals (dropdown options, tooltips, modals)
                    // through the row — ignore anything not physically inside the row.
                    if (!e.currentTarget.contains(e.target)) return;
                    // Ignore clicks on interactive children (buttons, links, checkboxes, selects).
                    if (e.target.closest('button, a, input, .ant-select, .ant-checkbox-wrapper')) return;
                    onRowClick(row);
                  },
                  className: styles.clickable,
                })
              : undefined
          }
        />
      )}
    </div>
  );
};

export default DataTable;
